import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { AdminUser } from '../lib/authService'
import { sendCounselorAssignmentSMS, sendUserCounselorAssignedSMS } from '../services/smsNotificationService'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertCircle,
  CheckCircle,
  Clock,
  MessageSquare,
  Phone,
  Mail,
  User,
  UserCheck,
  FileText,
  Plus,
  Loader2,
  Shield,
} from 'lucide-react'
import { useSubscription } from '@/hooks/useSubscription'
import { FeatureRestricted } from '@/components/subscription/FeatureRestricted'

interface CounselingRequest {
  id: string
  user_id: string
  church_id: string
  requester_name?: string
  email?: string
  phone?: string
  category: string
  urgency: 'low' | 'medium' | 'high' | 'urgent'
  description: string
  preferred_contact: 'phone' | 'email' | 'in_person'
  is_anonymous: boolean
  assigned_to?: string
  status: 'pending' | 'assigned' | 'in_progress' | 'completed' | 'cancelled'
  notes?: string
  created_at: string
  updated_at: string
  profiles?: {
    first_name: string
    last_name: string
    email: string
    phone: string
  }
  counselor?: {
    first_name: string
    last_name: string
  }
}

interface Counselor {
  id: string
  first_name: string
  last_name: string
  email: string
}

interface CounselingProps {
  user: AdminUser | null
}

export default function Counseling({ user: currentUser }: CounselingProps) {
  const { features, limits, loading: subscriptionLoading } = useSubscription(currentUser?.churchId)
  const [requests, setRequests] = useState<CounselingRequest[]>([])
  const [counselors, setCounselors] = useState<Counselor[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<string>('pending')
  const [selectedRequest, setSelectedRequest] = useState<CounselingRequest | null>(null)
  const [showAssignDialog, setShowAssignDialog] = useState(false)
  const [showNotesDialog, setShowNotesDialog] = useState(false)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [assignedCounselor, setAssignedCounselor] = useState('')
  const [notes, setNotes] = useState('')
  const [myCasesStatusFilter, setMyCasesStatusFilter] = useState<string>('all')
  const [myCasesSortBy, setMyCasesSortBy] = useState<'urgency' | 'date'>('urgency')
  const [newRequest, setNewRequest] = useState({
    requester_name: '',
    email: '',
    phone: '',
    category: 'spiritual',
    urgency: 'medium' as const,
    description: '',
    preferred_contact: 'phone' as const,
  })

  useEffect(() => {
    loadCounselingRequests()
    loadCounselors()
  }, [currentUser])

  const loadCounselingRequests = async () => {
    if (!currentUser?.churchId) return

    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('counseling_requests')
        .select(`
          *,
          profiles:user_id (
            first_name,
            last_name,
            email,
            phone
          ),
          counselor:assigned_to (
            first_name,
            last_name
          )
        `)
        .eq('church_id', currentUser.churchId)
        .order('created_at', { ascending: false })

      if (error) throw error
      setRequests(data || [])
    } catch (error) {
      console.error('Error loading counseling requests:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadCounselors = async () => {
    if (!currentUser?.churchId) return

    try {
      // Get admins, leaders, pastors, and dedicated counselors
      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email')
        .eq('church_id', currentUser.churchId)
        .in('role', ['admin', 'leader', 'pastor', 'counselor'])
        .order('first_name')

      if (error) throw error
      setCounselors(data || [])
    } catch (error) {
      console.error('Error loading counselors:', error)
    }
  }

  const handleAssignCounselor = async () => {
    if (!selectedRequest || !assignedCounselor) return

    try {
      // Find counselor details
      const counselor = counselors.find(c => c.id === assignedCounselor)
      if (!counselor) throw new Error('Counselor not found')

      const { error } = await supabase
        .from('counseling_requests')
        .update({
          assigned_to: assignedCounselor,
          status: 'assigned',
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedRequest.id)

      if (error) throw error

      // Get counselor's phone number for notification
      const { data: counselorProfile } = await supabase
        .from('profiles')
        .select('phone')
        .eq('id', assignedCounselor)
        .single()

      // Send SMS notification to counselor
      if (counselorProfile?.phone) {
        const counselorName = `${counselor.first_name} ${counselor.last_name}`
        const category = getCategoryLabel(selectedRequest.category)
        const urgency = selectedRequest.urgency

        await sendCounselorAssignmentSMS(
          counselorProfile.phone,
          counselorName,
          category,
          urgency
        ).catch(err => {
          console.error('Failed to send SMS to counselor:', err)
          // Don't fail the assignment if SMS fails
        })
      }

      // Send SMS notification to user (if not anonymous)
      if (!selectedRequest.is_anonymous) {
        const userPhone = selectedRequest.profiles?.phone || selectedRequest.phone
        if (userPhone) {
          const userName = selectedRequest.profiles
            ? `${selectedRequest.profiles.first_name} ${selectedRequest.profiles.last_name}`
            : selectedRequest.requester_name || 'Member'
          const counselorName = `${counselor.first_name} ${counselor.last_name}`

          await sendUserCounselorAssignedSMS(
            userPhone,
            userName,
            counselorName
          ).catch(err => {
            console.error('Failed to send SMS to user:', err)
            // Don't fail the assignment if SMS fails
          })
        }
      }

      setShowAssignDialog(false)
      setSelectedRequest(null)
      setAssignedCounselor('')
      await loadCounselingRequests()
    } catch (error) {
      console.error('Error assigning counselor:', error)
      alert('Failed to assign counselor')
    }
  }

  const handleUpdateStatus = async (requestId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('counseling_requests')
        .update({
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId)

      if (error) throw error
      await loadCounselingRequests()
    } catch (error) {
      console.error('Error updating status:', error)
      alert('Failed to update status')
    }
  }

  const handleAddNotes = async () => {
    if (!selectedRequest) return

    try {
      const { error } = await supabase
        .from('counseling_requests')
        .update({
          notes: notes,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedRequest.id)

      if (error) throw error

      setShowNotesDialog(false)
      setSelectedRequest(null)
      setNotes('')
      await loadCounselingRequests()
    } catch (error) {
      console.error('Error adding notes:', error)
      alert('Failed to add notes')
    }
  }

  const handleCreateRequest = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!currentUser?.churchId) return

    try {
      const { error } = await supabase
        .from('counseling_requests')
        .insert({
          church_id: currentUser.churchId,
          user_id: currentUser.id,
          requester_name: newRequest.requester_name,
          email: newRequest.email,
          phone: newRequest.phone,
          category: newRequest.category,
          urgency: newRequest.urgency,
          description: newRequest.description,
          preferred_contact: newRequest.preferred_contact,
          is_anonymous: false,
          status: 'pending'
        })

      if (error) throw error

      setShowCreateDialog(false)
      setNewRequest({
        requester_name: '',
        email: '',
        phone: '',
        category: 'spiritual',
        urgency: 'medium',
        description: '',
        preferred_contact: 'phone',
      })
      await loadCounselingRequests()
    } catch (error) {
      console.error('Error creating counseling request:', error)
      alert('Failed to create counseling request')
    }
  }

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      spiritual: 'Spiritual Guidance',
      marriage: 'Marriage & Family',
      addiction: 'Addiction & Recovery',
      grief: 'Grief & Loss',
      financial: 'Financial Guidance',
      mental_health: 'Mental Health',
      youth: 'Youth & Parenting',
      other: 'Other'
    }
    return labels[category] || category
  }

  const getUrgencyBadge = (urgency: string) => {
    const variants: Record<string, { variant: 'default' | 'secondary' | 'outline' | 'destructive', label: string }> = {
      low: { variant: 'outline', label: 'Low' },
      medium: { variant: 'secondary', label: 'Medium' },
      high: { variant: 'default', label: 'High' },
      urgent: { variant: 'destructive', label: 'Urgent' }
    }
    return variants[urgency] || variants.medium
  }

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: 'default' | 'secondary' | 'outline' | 'destructive', icon: React.ReactNode }> = {
      pending: { variant: 'secondary', icon: <Clock className="h-3 w-3 mr-1" /> },
      assigned: { variant: 'default', icon: <UserCheck className="h-3 w-3 mr-1" /> },
      in_progress: { variant: 'default', icon: <AlertCircle className="h-3 w-3 mr-1" /> },
      completed: { variant: 'outline', icon: <CheckCircle className="h-3 w-3 mr-1" /> },
      cancelled: { variant: 'destructive', icon: <CheckCircle className="h-3 w-3 mr-1" /> }
    }
    return variants[status] || variants.pending
  }

  const filterByStatus = (status: string) => {
    if (status === 'all') return requests
    if (status === 'my_cases') {
      // Filter to show only cases assigned to current user
      let filtered = requests.filter(r => r.assigned_to === currentUser?.id)

      // Apply status filter within My Cases
      if (myCasesStatusFilter !== 'all') {
        filtered = filtered.filter(r => r.status === myCasesStatusFilter)
      }

      // Apply sorting
      filtered = [...filtered].sort((a, b) => {
        if (myCasesSortBy === 'urgency') {
          const urgencyOrder = { urgent: 0, high: 1, medium: 2, low: 3 }
          return urgencyOrder[a.urgency] - urgencyOrder[b.urgency]
        } else {
          // Sort by date (newest first)
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        }
      })

      return filtered
    }
    return requests.filter(r => r.status === status)
  }

  const renderRequestCard = (request: CounselingRequest) => {
    const displayName = request.is_anonymous
      ? request.requester_name || 'Anonymous'
      : request.profiles
        ? `${request.profiles.first_name} ${request.profiles.last_name}`
        : request.requester_name || 'Unknown'

    const contactEmail = request.is_anonymous ? request.email : request.profiles?.email
    const contactPhone = request.is_anonymous ? request.phone : request.profiles?.phone

    const urgencyBadge = getUrgencyBadge(request.urgency)
    const statusBadge = getStatusBadge(request.status)

    return (
      <Card key={request.id} className="hover:shadow-md transition-shadow">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                {request.is_anonymous ? (
                  <Shield className="h-5 w-5" />
                ) : (
                  <span className="font-semibold text-sm">
                    {displayName.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div>
                <p className="font-semibold">{displayName}</p>
                <p className="text-sm text-muted-foreground">
                  {request.is_anonymous ? 'Anonymous Request' : contactEmail}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Badge variant={urgencyBadge.variant}>
                {urgencyBadge.label}
              </Badge>
              <Badge variant="secondary">
                {getCategoryLabel(request.category)}
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-muted-foreground whitespace-pre-wrap">{request.description}</p>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Badge variant={statusBadge.variant} className="flex items-center">
                {statusBadge.icon}
                {request.status.charAt(0).toUpperCase() + request.status.slice(1).replace('_', ' ')}
              </Badge>
            </div>
            {request.counselor && (
              <div className="flex items-center gap-2">
                <UserCheck className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">
                  {request.counselor.first_name} {request.counselor.last_name}
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            {contactPhone && (
              <div className="flex items-center gap-1">
                <Phone className="h-3 w-3" />
                <span>{contactPhone}</span>
              </div>
            )}
            {contactEmail && (
              <div className="flex items-center gap-1">
                <Mail className="h-3 w-3" />
                <span>{contactEmail}</span>
              </div>
            )}
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              <span>{new Date(request.created_at).toLocaleDateString()}</span>
            </div>
          </div>

          {request.notes && (
            <Card className="bg-muted/50">
              <CardContent className="p-4">
                <div className="flex items-start gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium mb-1">Notes</p>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{request.notes}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex gap-2 flex-wrap">
            {request.status === 'pending' && (
              <Button
                onClick={() => {
                  setSelectedRequest(request)
                  setShowAssignDialog(true)
                }}
                size="sm"
              >
                <UserCheck className="mr-2 h-4 w-4" />
                Assign Counselor
              </Button>
            )}

            {request.status === 'assigned' && (
              <Button
                onClick={() => handleUpdateStatus(request.id, 'in_progress')}
                size="sm"
              >
                Start Session
              </Button>
            )}

            {request.status === 'in_progress' && (
              <Button
                onClick={() => handleUpdateStatus(request.id, 'completed')}
                size="sm"
                variant="default"
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                Mark Complete
              </Button>
            )}

            <Button
              onClick={() => {
                setSelectedRequest(request)
                setNotes(request.notes || '')
                setShowNotesDialog(true)
              }}
              size="sm"
              variant="outline"
            >
              <FileText className="mr-2 h-4 w-4" />
              {request.notes ? 'Edit Notes' : 'Add Notes'}
            </Button>

            {request.status !== 'completed' && request.status !== 'cancelled' && (
              <Button
                onClick={() => handleUpdateStatus(request.id, 'cancelled')}
                size="sm"
                variant="ghost"
              >
                Cancel
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (loading || subscriptionLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  if (!features.advancedMinistry) {
    return (
      <FeatureRestricted
        featureName="Counseling System"
        description="Manage counseling requests, assign counselors, track sessions, and maintain case notes."
        currentPlan={limits?.plan || 'starter'}
        requiredPlan="growth"
        icon={<MessageSquare className="h-10 w-10 text-white" />}
      />
    )
  }

  const pendingCount = requests.filter(r => r.status === 'pending').length
  const assignedCount = requests.filter(r => r.status === 'assigned').length
  const inProgressCount = requests.filter(r => r.status === 'in_progress').length
  const completedCount = requests.filter(r => r.status === 'completed').length
  const myCasesCount = requests.filter(r => r.assigned_to === currentUser?.id).length

  return (
    <div className="flex flex-col gap-4 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Counseling Requests</h1>
          <p className="text-muted-foreground">
            Manage counseling and help requests from church members
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Request
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Pending</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pendingCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Assigned</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{assignedCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">In Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{inProgressCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Completed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{completedCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between gap-4">
          <TabsList>
            {myCasesCount > 0 && (
              <TabsTrigger value="my_cases">My Cases ({myCasesCount})</TabsTrigger>
            )}
            <TabsTrigger value="pending">Pending ({pendingCount})</TabsTrigger>
            <TabsTrigger value="assigned">Assigned ({assignedCount})</TabsTrigger>
            <TabsTrigger value="in_progress">In Progress ({inProgressCount})</TabsTrigger>
            <TabsTrigger value="completed">Completed ({completedCount})</TabsTrigger>
            <TabsTrigger value="all">All ({requests.length})</TabsTrigger>
          </TabsList>

          {/* My Cases Filters - Only show when on My Cases tab */}
          {activeTab === 'my_cases' && (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                <Label className="text-sm text-muted-foreground">Status:</Label>
                <Select value={myCasesStatusFilter} onValueChange={setMyCasesStatusFilter}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="assigned">Assigned</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Label className="text-sm text-muted-foreground">Sort:</Label>
                <Select value={myCasesSortBy} onValueChange={(value) => setMyCasesSortBy(value as 'urgency' | 'date')}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgency">By Urgency</SelectItem>
                    <SelectItem value="date">By Date</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>

        <TabsContent value={activeTab} className="space-y-4 mt-4">
          {filterByStatus(activeTab).length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-lg font-medium">No requests found</p>
                <p className="text-sm text-muted-foreground">
                  {activeTab === 'pending' && "You're all caught up!"}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filterByStatus(activeTab).map(renderRequestCard)}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Assign Counselor Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="!bg-white dark:!bg-slate-950 border border-border">
          <DialogHeader>
            <DialogTitle>Assign Counselor</DialogTitle>
            <DialogDescription>
              Select a counselor to handle this request
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Counselor</Label>
              <Select value={assignedCounselor} onValueChange={setAssignedCounselor}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a counselor" />
                </SelectTrigger>
                <SelectContent>
                  {counselors.map(counselor => (
                    <SelectItem key={counselor.id} value={counselor.id}>
                      {counselor.first_name} {counselor.last_name} - {counselor.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAssignCounselor} disabled={!assignedCounselor}>
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notes Dialog */}
      <Dialog open={showNotesDialog} onOpenChange={setShowNotesDialog}>
        <DialogContent className="!bg-white dark:!bg-slate-950 border border-border">
          <DialogHeader>
            <DialogTitle>Counseling Notes</DialogTitle>
            <DialogDescription>
              Add confidential notes about this counseling session
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Enter session notes, follow-up actions, etc..."
                rows={6}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNotesDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddNotes}>
              Save Notes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Request Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto !bg-white dark:!bg-slate-950 border border-border">
          <DialogHeader>
            <DialogTitle>New Counseling Request</DialogTitle>
            <DialogDescription>
              Create a counseling request on behalf of a church member
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateRequest}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="requester_name">Name *</Label>
                <Input
                  id="requester_name"
                  required
                  value={newRequest.requester_name}
                  onChange={(e) => setNewRequest({ ...newRequest, requester_name: e.target.value })}
                  placeholder="Enter name..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newRequest.email}
                    onChange={(e) => setNewRequest({ ...newRequest, email: e.target.value })}
                    placeholder="email@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={newRequest.phone}
                    onChange={(e) => setNewRequest({ ...newRequest, phone: e.target.value })}
                    placeholder="+1234567890"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select
                    value={newRequest.category}
                    onValueChange={(value) => setNewRequest({ ...newRequest, category: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="spiritual">Spiritual Guidance</SelectItem>
                      <SelectItem value="marriage">Marriage & Family</SelectItem>
                      <SelectItem value="addiction">Addiction & Recovery</SelectItem>
                      <SelectItem value="grief">Grief & Loss</SelectItem>
                      <SelectItem value="financial">Financial Guidance</SelectItem>
                      <SelectItem value="mental_health">Mental Health</SelectItem>
                      <SelectItem value="youth">Youth & Parenting</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="urgency">Urgency *</Label>
                  <Select
                    value={newRequest.urgency}
                    onValueChange={(value) => setNewRequest({ ...newRequest, urgency: value as any })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="preferred_contact">Preferred Contact Method</Label>
                <Select
                  value={newRequest.preferred_contact}
                  onValueChange={(value) => setNewRequest({ ...newRequest, preferred_contact: value as any })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="phone">Phone</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="in_person">In Person</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  required
                  value={newRequest.description}
                  onChange={(e) => setNewRequest({ ...newRequest, description: e.target.value })}
                  placeholder="Describe the help needed..."
                  rows={6}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
              >
                Cancel
              </Button>
              <Button type="submit">
                Create Request
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
